import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TooltipConfig, DataValue, ChartDimensions } from '../../types';

interface TooltipData {
  x: number;
  y: number;
  value: DataValue;
  label: string;
  datasetLabel: string;
  color: string;
  mouseX?: number;
  mouseY?: number;
}

interface TooltipProps {
  config: TooltipConfig;
  data: TooltipData | null;
  dimensions: ChartDimensions;
  containerRef: React.RefObject<SVGSVGElement>;
}

/**
 * Tooltip component - Displays information on hover
 * Follows cursor position exactly
 * Accessible and responsive
 */
export const Tooltip: React.FC<TooltipProps> = ({
  config,
  data,
  dimensions,
  containerRef,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!data || !tooltipRef.current || !containerRef.current) {
      setIsVisible(false);
      return;
    }

    const tooltip = tooltipRef.current;
    const container = containerRef.current.parentElement;
    
    if (!container) {
      setIsVisible(false);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    
    // Use exact mouse position if available, otherwise use bar center
    let mouseX = data.mouseX ?? (data.x + dimensions.padding.left);
    let mouseY = data.mouseY ?? (data.y + dimensions.padding.top);
    
    // Calculate tooltip position relative to container
    let x = mouseX;
    let y = mouseY;
    
    // Minimal offset from cursor - keeps tooltip close
    const offsetX = 4;
    const offsetY = 4;
    
    // Position tooltip to the right and below cursor by default
    x += offsetX;
    y += offsetY;
    
    // Adjust for right edge overflow
    if (x + tooltipRect.width > containerRect.width) {
      x = mouseX - tooltipRect.width - offsetX;
    }
    
    // Adjust for bottom edge overflow
    if (y + tooltipRect.height > containerRect.height) {
      y = mouseY - tooltipRect.height - offsetY;
    }
    
    // Ensure tooltip stays within container bounds
    x = Math.max(4, Math.min(x, containerRect.width - tooltipRect.width - 4));
    y = Math.max(4, Math.min(y, containerRect.height - tooltipRect.height - 4));
    
    setPosition({ x, y });
    setIsVisible(true);
  }, [data, dimensions, containerRef]);

  if (!config.enabled || !data) return null;

  const {
    backgroundColor = 'rgba(0, 0, 0, 0.85)',
    textColor = '#fff',
    fontSize = 12,
    fontFamily,
    borderColor,
    borderWidth,
    cornerRadius = 6,
    padding = 10,
    formatter,
  } = config;

  // Format the value for display
  const formatValue = (value: DataValue): string => {
    if (formatter) {
      return formatter(value, data.datasetLabel, data.label);
    }
    if (Array.isArray(value)) {
      return `${value[0]} — ${value[1]}`;
    }
    // Format numbers with locale
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  return (
    <div
      ref={tooltipRef}
      role="tooltip"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        backgroundColor,
        color: textColor,
        fontSize: Math.max(11, fontSize),
        fontFamily: fontFamily || 'inherit',
        padding: `${padding}px ${padding + 2}px`,
        borderRadius: cornerRadius,
        border: borderWidth ? `${borderWidth}px solid ${borderColor || 'rgba(255,255,255,0.2)'}` : undefined,
        pointerEvents: 'none',
        zIndex: 10000,
        whiteSpace: 'nowrap',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25), 0 2px 4px rgba(0,0,0,0.15)',
        transition: 'opacity 0.15s ease, transform 0.1s ease',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.95)',
        transformOrigin: 'top left',
        maxWidth: '280px',
        lineHeight: 1.4,
      }}
    >
      {/* Category label */}
      <div style={{ 
        fontWeight: 600, 
        marginBottom: 6,
        fontSize: fontSize + 1,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: 6,
      }}>
        {data.label}
      </div>
      
      {/* Dataset value */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 8,
      }}>
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            backgroundColor: data.color,
            borderRadius: 2,
            flexShrink: 0,
            border: '1px solid rgba(255,255,255,0.3)',
          }}
          aria-hidden="true"
        />
        <span style={{ fontWeight: 500 }}>{data.datasetLabel}:</span>
        <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
          {formatValue(data.value)}
        </span>
      </div>
    </div>
  );
};

export default Tooltip;
